import { FC, useMemo, useState } from "react";
import { ICouponForm } from "../../api/coupon.api";
import { ICoupon, ICouponType } from "../../model/ICoupon";
import Button from "../form/Button";
import Input from "../form/Input";

// --
// -- Dates are the store's, not the browser's: America/Chicago is the day
// -- boundary the storefront compares an expiry against, so a date picked here
// -- late in the evening in Europe has to mean the same day it will mean there.
// --
// -- en-CA formats as yyyy-MM-dd, which is what a date input reads and writes.
// --
const asStoreDay = (date: Date): string => new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'America/Chicago',
}).format(date);

const todayInStoreZone = (): string => asStoreDay(new Date());

/** The default expiry for a new coupon: a month out. */
const thirtyDaysOut = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return asStoreDay(date);
};

interface IFormState {
  name: string;
  code: string;
  typeId: string;
  value1: string;
  value2: string;
  partCategoryId: string;
  expiresOn: string;
}

const stateFrom = (coupon?: ICoupon | null): IFormState => coupon
  ? {
    name: coupon.name ?? '',
    code: coupon.code ?? '',
    typeId: String(coupon.typeId ?? 2),
    value1: String(coupon.value1 ?? ''),
    value2: String(coupon.value2 ?? 0),
    partCategoryId: coupon.partCategoryId ? String(coupon.partCategoryId) : '',
    expiresOn: coupon.expiresOn ?? '',
  }
  : {
    name: '',
    code: '',
    typeId: '2',
    value1: '',
    value2: '0',
    partCategoryId: '',
    expiresOn: thirtyDaysOut(),
  };

/**
 * Create or edit one coupon.
 *
 * <p>The form changes shape with the type, because the stored columns mean
 * different things for different types: value1 is a percentage for two of them
 * and an amount of money for the other two, and only the part-restricted types
 * read a category. Labelling it one fixed way would be wrong half the time.
 */
const CouponForm: FC<{
  coupon?: ICoupon | null,
  types: ICouponType[],
  loading?: boolean,
  onSubmit: (form: ICouponForm) => void,
}> = ({ coupon, types, loading, onSubmit }) => {
  const [state, setState] = useState<IFormState>(() => stateFrom(coupon));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (key: keyof IFormState) => (e: unknown) =>
    setState((prev) => ({
      ...prev,
      [key]: (e as React.ChangeEvent<HTMLInputElement>)?.target?.value ?? '',
    }));

  const type = useMemo(
    () => types.find(({ id }) => String(id) === state.typeId),
    [types, state.typeId],
  );

  const handleSubmit = () => {
    const found: Record<string, string> = {};

    if (!state.name.trim()) found.name = 'A name is required';
    if (!state.code.trim()) found.code = 'A code is required';
    else if (state.code.trim().length > 16) found.code = 'At most 16 characters';

    const value1 = Number(state.value1);
    if (!state.value1.trim() || Number.isNaN(value1) || value1 <= 0) {
      found.value1 = 'Has to be more than zero';
    } else if (type?.percentage && value1 > 100) {
      found.value1 = 'A percentage cannot be over 100';
    }

    const value2 = Number(state.value2 || 0);
    if (Number.isNaN(value2) || value2 < 0) found.value2 = 'Cannot be negative';

    const partCategoryId = Number(state.partCategoryId || 0);
    if (type?.partRestricted && (!partCategoryId || partCategoryId <= 0)) {
      // Without it the storefront matches the cart against a zero, finds
      // nothing and refuses the coupon - so it could never apply to anything.
      found.partCategoryId = 'Required for this type';
    }

    if (!state.expiresOn) found.expiresOn = 'An expiry date is required';

    setErrors(found);
    if (Object.keys(found).length) return;

    onSubmit({
      name: state.name.trim(),
      code: state.code.trim(),
      typeId: Number(state.typeId),
      value1,
      value2,
      partCategoryId: type?.partRestricted ? partCategoryId : 0,
      expiresOn: state.expiresOn,
    });
  };

  const isPastDate = !!state.expiresOn && state.expiresOn < todayInStoreZone();

  return (
    <div className="w-full grid grid-cols-12 gap-x-5 gap-y-6">
      <Input
        label="Name"
        name="name"
        value={state.name}
        handle={set('name')}
        error={errors.name}
        disabled={loading}
        maxLength={64}
        description="Shown in the admin tools only, never to the customer."
        className="col-span-12"
      />

      <Input
        label="Code"
        name="code"
        value={state.code}
        handle={set('code')}
        error={errors.code}
        disabled={loading}
        maxLength={16}
        description="What the customer types at checkout. 16 characters at most, and it has to be unique."
        className="col-span-6"
      />

      <Input
        label="Type"
        name="typeId"
        value={state.typeId}
        options={types.map(({ id, label }) => ({ title: label, value: String(id) }))}
        handle={set('typeId')}
        disabled={loading}
        className="col-span-6"
      />

      <Input
        label={type?.percentage ? 'Discount (%)' : 'Discount ($)'}
        name="value1"
        value={state.value1}
        handle={set('value1')}
        error={errors.value1}
        disabled={loading}
        className="col-span-6"
      />

      <Input
        label="Minimum order subtotal ($)"
        name="value2"
        value={state.value2}
        handle={set('value2')}
        error={errors.value2}
        disabled={loading}
        description="0 means any order qualifies."
        className="col-span-6"
      />

      {!!type?.partRestricted && (
        <Input
          label="Part category id"
          name="partCategoryId"
          value={state.partCategoryId}
          handle={set('partCategoryId')}
          error={errors.partCategoryId}
          disabled={loading}
          description="This type only discounts one category, and the storefront matches on this id."
          className="col-span-6"
        />
      )}

      <Input
        label="Last day it works"
        type="date"
        name="expiresOn"
        value={state.expiresOn}
        handle={set('expiresOn')}
        error={errors.expiresOn}
        disabled={loading}
        description={isPastDate
          // Allowed on purpose: extending a coupon that has already run out
          // means opening one with a date in the past.
          ? 'This date has passed, so the coupon will not work.'
          : 'The coupon works all through this day, in store time.'}
        className="col-span-6"
      />

      <div className="col-span-12 flex justify-end gap-3 mt-2">
        <Button isNotFullWidth type="submit" disabled={loading} onClick={handleSubmit}>
          {coupon ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
};

export default CouponForm;
